// JogIdsToRetrieveAllWspEvents is based on the SetJogIds(DispatchEvent data) function,
// in vcc-app-dispatch, where JogId is set for WSP dispatch events:
// https://dev.azure.com/WSDOT/VCC/_git/vcc-app-dispatch?path=/src/functions/DispatchEventPrepare/Function.cs&version=GBjog-main&line=160&lineEnd=161&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents

import { AgencyCode, AgencyId, AreaCode, RegionCode } from ".";

// Labels that can be edited
enum WspDistrictLabel {
  D1_TAC = "TAC", // Tacoma
  D2_SEA = "SEA", // Seattle
  D3_YAK = "YAK", // Yakima
  D4_SPO = "SPO", // Spokane
  D5_VAN = "VAN", // Vancouver
  D6_WEN = "WEN", // Wenatchee
  D7_NW = "NW",   // Northwest
  D8_PEN = "PEN", // Kitsap Peninsula
}

const AgencyDelimeter = ": ";

// IDs that should not be edited, because they're saved in UserPreferences
export class WspDistrictAgencyId  {
  static TAC_D1 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D1
  static SEA_D2 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D2
  static YAK_D3 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D3
  static SPO_D4 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D4
  static VAN_D5 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D5
  static WEN_D6 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D6
  static NW_D7 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D7
  static PEN_D8 = AgencyCode.WSP + AgencyDelimeter + AreaCode.WSP_D8
}

// Labels that can be edited
export class WspAgencyLabel {
  // TODO: Consider whether we still need these as AgencyCodes or not.
  // May just need the AgencyRegionCode values. UI could use the combo of both enums, merhaps.
  // E.g., AgencyCode.WSP + ":" + AgencyRegionCode.NW
  static WSP = AgencyCode.WSP;
  static TAC_D1 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D1_TAC;  // District 1 (Pierce and Thurston county)
  static SEA_D2 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D2_SEA;  // District 2 (King County)
  static YAK_D3 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D3_YAK;  // District 3
  static SPO_D4 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D4_SPO;  // District 4
  static VAN_D5 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D5_VAN;  // District 5
  static WEN_D6 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D6_WEN;  // District 6
  static NW_D7 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D7_NW;    // District 7
  static PEN_D8 = AgencyCode.WSP + AgencyDelimeter + WspDistrictLabel.D8_PEN;  // District 8

  // Unused
  static I5 = AgencyCode.WSP + AgencyDelimeter + AreaCode.I5; // Subset of District 2

  // Obsolete
  static S_D3 = AgencyCode.WSP + AgencyDelimeter + RegionCode.S;   // District 3 // todo: rename to S_D3_obsolete
  static E_D4 = AgencyCode.WSP + AgencyDelimeter + RegionCode.E;   // District 4 // todo: rename to E_D4_obsolete
  static SW_D5 = AgencyCode.WSP + AgencyDelimeter + RegionCode.SW; // District 5 // todo: rename to SW_D5_obsolete
  static NC_D6 = AgencyCode.WSP + AgencyDelimeter + RegionCode.NC; // District 6 // todo: rename to NC_D6_obsolete

  static WSP_North = "WSP-North"; // TODO: Re-work? This is a hard coded menu item value when inviting or editing a user. Needed still, or replaced by "WSP: NC" / "WSP: N"?
}

// Purposely exporting this map for readability. Where it's used, you can
// tell if there's not a converted value the existing value should be used.
export const AgencyFilterConversionMap = new Map<AgencyId, string>([  
  // Obsolete WSP filters
  [WspAgencyLabel.TAC_D1, WspDistrictAgencyId.TAC_D1],
  [WspAgencyLabel.SEA_D2, WspDistrictAgencyId.SEA_D2],
  [WspAgencyLabel.S_D3, WspDistrictAgencyId.YAK_D3],  
  [WspAgencyLabel.E_D4, WspDistrictAgencyId.SPO_D4],
  [WspAgencyLabel.SW_D5, WspDistrictAgencyId.VAN_D5],
  [WspAgencyLabel.NC_D6, WspDistrictAgencyId.WEN_D6],
  [WspAgencyLabel.NW_D7, WspDistrictAgencyId.NW_D7],
  [WspAgencyLabel.PEN_D8, WspDistrictAgencyId.PEN_D8],  
]);

// Purposely not exporting the map
const AgencyFilterLabels = new Map<AgencyId, string>([
  [WspDistrictAgencyId.TAC_D1, WspAgencyLabel.TAC_D1],
  [WspDistrictAgencyId.SEA_D2, WspAgencyLabel.SEA_D2],
  [WspDistrictAgencyId.YAK_D3, WspAgencyLabel.YAK_D3],
  [WspDistrictAgencyId.SPO_D4, WspAgencyLabel.SPO_D4],
  [WspDistrictAgencyId.VAN_D5, WspAgencyLabel.VAN_D5],
  [WspDistrictAgencyId.WEN_D6, WspAgencyLabel.WEN_D6],
  [WspDistrictAgencyId.NW_D7, WspAgencyLabel.NW_D7],
  [WspDistrictAgencyId.PEN_D8, WspAgencyLabel.PEN_D8],
]);

// Using this function instead of direct map access to uniformly return the agencyId if there is no label.
// This works currently because all of the filters that aren't in the map use their AreaCode as the ID.
export const GetAgencyFilterLabel = (agencyId: AgencyId): string => {
  return AgencyFilterLabels.get(agencyId) || agencyId as string;
}

// Used in agency-chip
export const getWspLabel = (area: AreaCode | string = ""): WspAgencyLabel => {
  switch (area) {
    case AreaCode.WSP_D1: return WspAgencyLabel.TAC_D1;
    case AreaCode.WSP_D2:
    case AreaCode.I5: // The area value of D2 events within specific coordinates
      return WspAgencyLabel.SEA_D2;
    case AreaCode.WSP_D3: return WspAgencyLabel.YAK_D3;
    case AreaCode.WSP_D4: return WspAgencyLabel.SPO_D4;
    case AreaCode.WSP_D5: return WspAgencyLabel.VAN_D5;
    case AreaCode.WSP_D6: return WspAgencyLabel.WEN_D6;
    case AreaCode.WSP_D7: return WspAgencyLabel.NW_D7;
    case AreaCode.WSP_D8: return WspAgencyLabel.PEN_D8;
    default:
      return WspAgencyLabel.WSP;
  }
};

// Used in filter click handler
export const getWspDistrictAgencyId = (area: AreaCode | string = ""): WspDistrictAgencyId | undefined => {
  switch (area) {
    case AreaCode.WSP_D1: return WspDistrictAgencyId.TAC_D1;
    case AreaCode.WSP_D2:
    case AreaCode.I5: // The area value of D2 events within specific coordinates
      return WspDistrictAgencyId.SEA_D2;
    case AreaCode.WSP_D3: return WspDistrictAgencyId.YAK_D3;
    case AreaCode.WSP_D4: return WspDistrictAgencyId.SPO_D4;
    case AreaCode.WSP_D5: return WspDistrictAgencyId.VAN_D5;
    case AreaCode.WSP_D6: return WspDistrictAgencyId.WEN_D6;
    case AreaCode.WSP_D7: return WspDistrictAgencyId.NW_D7;
    case AreaCode.WSP_D8: return WspDistrictAgencyId.PEN_D8;
    default: return undefined;
  }
};